
import { createAsyncThunk } from "@reduxjs/toolkit";
import { productsService } from "../services/products.service";
import { handleRejectError } from "../../../utils/api/api.utils";
import { ApiError } from "../../../utils/api/api.service";
import {
  CreateProductData,
  UpdateProductData,
  ProductFilters,
  ProductsApiResponse,
  ProductsState,
  Product,
  ProductReviewsSummary,
  SearchProductsResponse,
  Category,
  Location,
} from "../interfaces/products.interface";
import {
  MyListingsProduct,
  Listing,
  DraftListing,
  PaginatedMyProducts,
  MyListingsTab,
  FetchMyListingsResult,
  MyListingsStats,
  MyListingsPagination,
} from "../../myListings/interfaces/myListings.interface";
import { getListingDisplayStatus } from "../../../utils/helpers/listingStatus.helper";

export interface ValidationErrorItem {
  path: string;
  msg: string;
}

type MyListingsBackendData = PaginatedMyProducts;
type MyListingsBackendResponse = {
  success: boolean;
  data?: MyListingsBackendData;
  message?: string;
};

function getValidationErrors(error: unknown): { message: string; errors: ValidationErrorItem[] } | null {
  // Backend sends: { message: string, errors: { [fieldPath]: string } } (object form)
  const err = error as ApiError & { response?: { data?: { errors?: Record<string, string> | Array<{ path?: string; param?: string; msg?: string }>; message?: string } } };
  const data = err?.response?.data;
  if (!data?.errors) return null;

  // Object form: { title: "Title is required", price: "Price must be..." }
  if (!Array.isArray(data.errors) && typeof data.errors === 'object') {
    const items: ValidationErrorItem[] = Object.entries(data.errors).map(([path, msg]) => ({ path, msg: String(msg) }));
    if (items.length === 0) return null;
    return { message: data.message || "Validation failed", errors: items };
  }

  // Array form: [{ path: "title", msg: "Title is required" }]
  if (Array.isArray(data.errors) && data.errors.length > 0) {
    type ApiErrorItem = { path?: string; param?: string; msg?: string };
    return {
      message: data.message || "Validation failed",
      errors: (data.errors as ApiErrorItem[]).map((e) => ({ path: e.path ?? e.param ?? "", msg: e.msg ?? "" })),
    };
  }

  return null;
}

// Fetch products with filters (Category-scoped)
export const fetchProducts = createAsyncThunk<
  ProductsApiResponse & { category?: string; append?: boolean },
  { category?: string; page?: number; limit?: number; append?: boolean;[key: string]: any } | undefined,
  { state: { products: ProductsState } }
>(
  "products/fetchProducts",
  async (params, { rejectWithValue }) => {
    try {
      const category = params?.category || "All";
      const page = params?.page || 1;
      const limit = params?.limit || 15;

      const finalParams = {
        ...params,
        category: category === "All" ? undefined : category,
        page,
        limit,
      };

      const response = await productsService.getProducts(finalParams as any);

      if (response.success && response.data) {
        // Map backend response specifically picking items and currentPage
        const { items, currentPage, ...rest } = response.data;
        return {
          ...rest,
          items: items || [],
          page: currentPage || params?.page || 1,
          category: params?.category || "All",
          append: params?.append ?? false
        };
      }
      return rejectWithValue(response.message || "Failed to fetch products");
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
  {
    condition: (params, { getState }) => {
      const { products } = getState();
      const category = params?.category || "All";
      const catState = products.categories[category];

      if (!catState) return true;

      // If already loading this category, skip
      if (catState.loading) return false;

      // If already initialized and requesting same page, skip
      if (catState.initialized && params?.page === catState.page) {
        return false;
      }

      // If requesting beyond total pages, skip
      if (catState.initialized && params?.page && params.page > catState.totalPages) {
        return false;
      }

      return true;
    },
  }
);

// Fetch single product by ID
export const fetchProductById = createAsyncThunk<Product, string>(
  "products/fetchProductById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await productsService.getProduct(id);
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue(response.message || "Failed to fetch product");
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Create new product (full validation)
export const createProduct = createAsyncThunk<
  Product,
  CreateProductData,
  { rejectValue: string | { message: string; errors: ValidationErrorItem[] } }
>(
  "products/createProduct",
  async (productData, { rejectWithValue }) => {
    try {
      const response = await productsService.createProduct(productData);
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue(response.message || "Failed to create product");
    } catch (error: unknown) {
      const validation = getValidationErrors(error);
      if (validation) return rejectWithValue(validation);
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Create draft (title only required)
export const createDraft = createAsyncThunk<
  Product,
  Partial<CreateProductData> & { title: string },
  { rejectValue: string | { message: string; errors: ValidationErrorItem[] } }
>(
  "products/createDraft",
  async (draftData, { rejectWithValue }) => {
    try {
      const response = await productsService.createDraft(draftData);
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue(response.message || "Failed to save draft");
    } catch (error: unknown) {
      const validation = getValidationErrors(error);
      if (validation) return rejectWithValue(validation);
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Update product (full validation)
export const updateProduct = createAsyncThunk<
  Product,
  { id: string; data: UpdateProductData },
  { rejectValue: string | { message: string; errors: ValidationErrorItem[] } }
>("products/updateProduct", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await productsService.updateProduct(id, data);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.message || "Failed to update product");
  } catch (error: unknown) {
    const validation = getValidationErrors(error);
    if (validation) return rejectWithValue(validation);
    return handleRejectError(rejectWithValue, error);
  }
});

// Update draft (relaxed validation)
export const updateDraft = createAsyncThunk<
  Product,
  { id: string; data: Partial<UpdateProductData> & { title: string } },
  { rejectValue: string | { message: string; errors: ValidationErrorItem[] } }
>("products/updateDraft", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await productsService.updateDraft(id, data);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.message || "Failed to update draft");
  } catch (error: unknown) {
    const validation = getValidationErrors(error);
    if (validation) return rejectWithValue(validation);
    return handleRejectError(rejectWithValue, error);
  }
});

// Update product status
export const updateProductStatus = createAsyncThunk<
  Product,
  { id: string; status: string }
>(
  "products/updateProductStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await productsService.updateProductStatus(id, status);
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue(
        response.message || "Failed to update product status",
      );
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Delete product
export const deleteProduct = createAsyncThunk(
  "products/deleteProduct",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await productsService.deleteProduct(id);
      if (response.success) {
        return {
          message: response.message || "Product deleted successfully",
          id,
        };
      }
      return rejectWithValue(response.message || "Failed to delete product");
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Fetch user's products
export const fetchUserProducts = createAsyncThunk<
  Product[],
  { userId?: string; query?: Record<string, string> } | undefined
>("products/fetchUserProducts", async (params, { rejectWithValue }) => {
  try {
    const userId = params?.userId;
    const query = {
      limit: "100",
      page: "1",
      ...params?.query,
    };
    const response = await productsService.getUserProducts(userId, query);
    if (response.success && response.data) {
      // Handle both array and paginated response
      return Array.isArray(response.data)
        ? response.data
        : response.data.products || [];
    }
    return rejectWithValue(response.message || "Failed to fetch user products");
  } catch (error: any) {
    return handleRejectError(rejectWithValue, error);
  }
});

// Search products
export const searchProducts = createAsyncThunk<
  SearchProductsResponse,
  { query: string; filters?: any; page?: number; limit?: number; append?: boolean }
>(
  "products/searchProducts",
  async ({ query, filters, page, limit }, { rejectWithValue }) => {
    try {
      const response = await productsService.searchProducts(
        query,
        filters,
        page ?? 1,
        limit ?? 100,
      );
      if (response.success && response.data) {
        // Send search event for analytics
        try {
          const resultsCount = response.data.products?.length || 0;
          const body = {
            query: query.trim(),
            category: filters?.category || null,
            location: filters?.location || null,
            country: filters?.country || null,
            filters: filters || null,
            resultsCount,
          };
          const apiBase = process.env.REACT_APP_API_URL || "/api";
          await fetch(`${apiBase.replace(/\/$/, '')}/search-events`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
        } catch (error) {
          console.warn('Failed to send search event', error);
        }
        return response.data;
      }
      return rejectWithValue(response.message || "Failed to search products");
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Fetch categories
export const fetchCategories = createAsyncThunk<Category[], void>(
  "products/fetchCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await productsService.getCategories();
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue(response.message || "Failed to fetch categories");
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Fetch locations
export const fetchLocations = createAsyncThunk<Location[], void>(
  "products/fetchLocations",
  async (_, { rejectWithValue }) => {
    try {
      const response = await productsService.getLocations();
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue(response.message || "Failed to fetch locations");
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Upload product image
export const uploadProductImage = createAsyncThunk<
  any,
  { productId: string; file: File }
>(
  "products/uploadProductImage",
  async ({ productId, file }, { rejectWithValue }) => {
    try {
      const response = await productsService.uploadProductImage(
        productId,
        file,
      );
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message || "Failed to upload image");
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Track product view
export const trackProductView = createAsyncThunk<void, string>(
  "products/trackProductView",
  async (productId, { rejectWithValue }) => {
    try {
      await productsService.trackProductView(productId);
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Fetch single product by slug
export const fetchProductBySlug = createAsyncThunk<Product, string>(
  "products/fetchProductBySlug",
  async (slug, { rejectWithValue }) => {
    try {
      const response = await productsService.getProductBySlug(slug);
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue(response.message || "Failed to fetch product");
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Fetch product reviews
export const fetchProductReviews = createAsyncThunk<
  ProductReviewsSummary,
  { productId: string; currentUserId?: string }
>("products/fetchProductReviews", async ({ productId, currentUserId }, { rejectWithValue }) => {
  try {
    const response = await productsService.getProductReviews(productId, currentUserId);

    if (response.success && response.data != null) {
      const data = response.data as any;

      // Get reviews array (handle different possible structures)
      let reviews: any[] = [];
      if (Array.isArray(data)) {
        reviews = data;
      } else if (Array.isArray(data.reviews)) {
        reviews = data.reviews;
      } else if (data.data && Array.isArray(data.data)) {
        reviews = data.data;
      } else {
        reviews = [];
      }

      // Calculate statistics from reviews if not provided
      const totalReviews = reviews.length;
      const averageRating = totalReviews > 0
        ? reviews.reduce((sum: number, review: any) => sum + (review.rating || 0), 0) / totalReviews
        : 0;

      // Calculate rating distribution
      const ratingDistribution: { [key: number]: number } = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      reviews.forEach((review: any) => {
        const rating = Math.floor(review.rating);
        if (rating >= 1 && rating <= 5) {
          ratingDistribution[rating] = (ratingDistribution[rating] || 0) + 1;
        }
      });

      // Map reviews with helpfulness fields
      const mappedReviews = reviews.map((review: any) => ({
        id: review.id,
        rating: review.rating,
        comment: review.comment,
        createdAt: review.createdAt,
        user: review.user || review.reviewer,
        helpfulYesCount: review.helpfulYesCount || review.helpfulYes || 0,
        helpfulNoCount: review.helpfulNoCount || review.helpfulNo || 0,
        userVote: review.userVote || null,
      }));

      return {
        reviews: mappedReviews,
        averageRating: parseFloat(averageRating.toFixed(1)),
        totalReviews,
        ratingDistribution,
      };
    }

    return rejectWithValue(response.message || "Failed to fetch reviews");
  } catch (error: any) {
    console.error('Error fetching product reviews:', error);
    if (error?.statusCode === 404 || error?.response?.status === 404) {
      return {
        reviews: [],
        averageRating: 0,
        totalReviews: 0,
        ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      };
    }
    return rejectWithValue(error?.message || "Failed to fetch reviews");
  }
});

// Post product review
export const postProductReview = createAsyncThunk<
  void,
  { productId: string; rating: number; comment?: string }
>(
  "products/postProductReview",
  async ({ productId, rating, comment }, { rejectWithValue }) => {
    try {
      const response = await productsService.postProductReview(productId, {
        rating,
        comment: comment || undefined,
      });
      if (!response.success) {
        return rejectWithValue(response.message || "Failed to post review");
      }
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Submit review helpfulness (like/dislike)
// isHelpful: true = helpful, false = not helpful.
// Sending the same value twice will toggle the vote off on the backend.
export const submitReviewHelpfulness = createAsyncThunk<
  { reviewId: string; helpfulYesCount: number; helpfulNoCount: number; userVote: "yes" | "no" | null },
  { reviewId: string; isHelpful: boolean }
>(
  "products/submitReviewHelpfulness",
  async ({ reviewId, isHelpful }, { rejectWithValue }) => {
    try {
      const response = await productsService.submitReviewHelpfulness(reviewId, isHelpful);
      const data = response?.data as any;
      const payload = (data?.data ?? data) as any;
      return {
        reviewId,
        helpfulYesCount: Number(payload?.helpfulYesCount ?? 0),
        helpfulNoCount: Number(payload?.helpfulNoCount ?? 0),
        userVote: (payload?.userVote ?? null) as "yes" | "no" | null,
      };
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Fetch helpfulness counts for a single review (also returns user's vote if authenticated)
export const fetchReviewHelpfulnessCounts = createAsyncThunk<
  { reviewId: string; helpfulYesCount: number; helpfulNoCount: number; userVote: "yes" | "no" | null },
  string
>(
  "products/fetchReviewHelpfulnessCounts",
  async (reviewId, { rejectWithValue }) => {
    try {
      const response = await productsService.getReviewHelpfulnessCounts(reviewId);
      const data = response?.data as any;
      const payload = (data?.data ?? data) as any;
      return {
        reviewId,
        helpfulYesCount: Number(payload?.helpfulYesCount ?? 0),
        helpfulNoCount: Number(payload?.helpfulNoCount ?? 0),
        userVote: (payload?.userVote ?? null) as "yes" | "no" | null,
      };
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Fetch seller's other products (for product detail page)
export const fetchSellerProducts = createAsyncThunk<
  Product[],
  { sellerId: string; excludeProductId?: string }
>(
  "products/fetchSellerProducts",
  async ({ sellerId, excludeProductId }, { rejectWithValue }) => {
    try {
      const query: Record<string, string> = { limit: "12" };
      if (excludeProductId) query.exclude = excludeProductId;
      const response = await productsService.getUserProducts(sellerId, query);
      if (response.success && response.data) {
        const products = response.data.products || response.data || [];
        return Array.isArray(products) ? products : [];
      }
      return [];
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Fetch related products by category (for product detail page)
export const fetchRelatedProducts = createAsyncThunk<
  Product[],
  { category: string; excludeProductId?: string }
>(
  "products/fetchRelatedProducts",
  async ({ category, excludeProductId }, { rejectWithValue }) => {
    try {
      const response = await productsService.getProducts({
        category,
        limit: 12,
        page: 1,
      });
      if (response.success && response.data) {
        const list = response.data.products || [];
        const filtered = excludeProductId
          ? list.filter((p: Product) => p.id !== excludeProductId)
          : list;
        return filtered.slice(0, 12);
      }
      return [];
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// --- My Listings (consolidated from myListings feature) ---

function getFirstProductImageUrl(images: any[] | undefined): string {
  if (!images || !Array.isArray(images) || images.length === 0) return "";
  const first = images[0];
  if (!first) return "";
  if (typeof first === "string") return first;
  return (first.url ?? first.viewUrl ?? "") || "";
}

function productToListing(product: MyListingsProduct): Listing {
  const { status: listingStatus, daysLeft } = getListingDisplayStatus(product);
  return {
    id: product.id,
    title: product.title,
    image: getFirstProductImageUrl(product.images) || "",
    rating: product.averageRating ?? 0,
    reviews: product.reviewCount ?? 0,
    price: product.price?.toString() || "0",
    currency: product.currency || "GBP",
    createdAt: new Date(product.createdAt).getTime(),
    priceValue: product.price || 0,
    messages: product.messageCount ?? 0,
    category: product.category || "Uncategorized",
    status: listingStatus,
    daysLeft,
  };
}

export const fetchMyListings = createAsyncThunk<
  FetchMyListingsResult,
  { page: number; limit: number; activeTab: MyListingsTab },
  { rejectValue: string }
>(
  "products/fetchMyListings",
  async ({ page, limit, activeTab }, { rejectWithValue }) => {
    try {
      let status = "";
      if (activeTab === "drafts") status = "DRAFT";
      if (activeTab === "published") status = "PUBLISHED";
      if (activeTab === "sold") status = "SOLD";

      const response = await productsService.getMyProducts({
        page,
        limit,
        ...(status && { status }),
      });

      const raw = response as any;
      if (!raw.success) {
        return rejectWithValue(raw.message || "Failed to fetch listings");
      }

      let products = [];
      if (Array.isArray(raw.data)) {
        products = raw.data;
      } else {
        const payloadData = raw.data || {};
        products = payloadData.items || payloadData.products || [];
      }
      const listings: Listing[] = products.map(productToListing);

      const paginationSource = raw.pagination || (raw.data && raw.data.pagination) || {};
      const pagination: MyListingsPagination = {
        total: paginationSource.total || (raw.data && raw.data.total) || 0,
        page: paginationSource.page || paginationSource.currentPage || (raw.data && (raw.data.currentPage || raw.data.page)) || page,
        limit: paginationSource.limit || (raw.data && raw.data.limit) || limit,
        totalPages: paginationSource.totalPages || (raw.data && raw.data.totalPages) || 1,
      };

      const stats: MyListingsStats = raw.stats ?? (raw.data && raw.data.stats) ?? {
        total: pagination.total,
        drafts: 0,
        published: 0,
        sold: 0,
      };

      const result: FetchMyListingsResult = {
        products,
        listings,
        pagination,
        stats,
      };
      return result;
    } catch (error: unknown) {
      if (error instanceof ApiError) {
        return rejectWithValue(error.message);
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message || "An unexpected error occurred");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const fetchDrafts = createAsyncThunk<
  DraftListing[],
  void,
  { rejectValue: string }
>(
  "products/fetchDrafts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await productsService.getMyProducts({
        status: "DRAFT",
      });
      const raw = response as any;
      if (!raw?.success || !raw?.data) {
        return rejectWithValue(raw?.message || "Failed to fetch drafts");
      }
      let products = [];
      if (Array.isArray(raw.data)) {
        products = raw.data;
      } else {
        const payloadData = raw.data || {};
        products = payloadData.items || payloadData.products || [];
      }
      const drafts: DraftListing[] = products.map((product: MyListingsProduct) => {
        const imageUrls = (product.images || [])
          .map((img: any) => (img && typeof img === "object" ? (img.url ?? img.viewUrl ?? "") : typeof img === "string" ? img : ""))
          .filter((u: string) => u);
        const firstImage = imageUrls[0];
        return {
          id: product.id,
          title: product.title || "Undefined",
          price: product.price?.toString() || "N/A",
          currency: product.currency || "GBP",
          image: firstImage || "",
          description: product.description || "",
          country: product.origin || "Cameroon",
          flag: "",
          quantity: product.quantity ?? 0,
          category: product.category || "",
          location: product.location || "",
          images: imageUrls.length > 0 ? imageUrls : undefined,
          deliveryAvailable: false,
          origin: product.origin || "",
        };
      });
      return drafts;
    } catch (error: unknown) {
      if (error instanceof ApiError) {
        return rejectWithValue(error.message);
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message || "An unexpected error occurred");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);
